// Performance Utilities - Mobile Detection & Reduced Motion
import { useState, useEffect } from 'react';

/**
 * Check if device is mobile based on screen width
 */
export const isMobileDevice = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
};

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Check if device is low-end (heuristic based on hardware concurrency)
 */
export const isLowEndDevice = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    // Consider low-end if less than 4 cores
    return (navigator.hardwareConcurrency || 4) < 4;
};

/**
 * Hook to detect if animations should be disabled
 * Returns true if animations should be disabled (mobile or reduced motion preference)
 */
export const useReducedMotion = (): boolean => {
    const [shouldReduce, setShouldReduce] = useState<boolean>(false);

    useEffect(() => {
        const checkMotion = () => {
            const isMobile = isMobileDevice();
            const prefersReduced = prefersReducedMotion();
            const isLowEnd = isLowEndDevice();

            setShouldReduce(isMobile || prefersReduced || isLowEnd);
        };

        checkMotion();

        // Listen for resize events
        window.addEventListener('resize', checkMotion);

        // Listen for reduced motion preference changes
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        mediaQuery.addEventListener('change', checkMotion);

        return () => {
            window.removeEventListener('resize', checkMotion);
            mediaQuery.removeEventListener('change', checkMotion);
        };
    }, []);

    return shouldReduce;
};

/**
 * Hook to check if on mobile device
 */
export const useIsMobile = (): boolean => {
    const [isMobile, setIsMobile] = useState<boolean>(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return isMobile;
};

/**
 * Get animation duration based on device
 * Returns 0 for mobile (instant), normal duration for desktop
 */
export const getAnimationDuration = (normalDuration: number): number => {
    if (isMobileDevice() || prefersReducedMotion()) {
        return 0;
    }
    return normalDuration;
};

/**
 * CSS class helper for conditional animations
 */
export const getAnimationClass = (animationClass: string): string => {
    if (isMobileDevice() || prefersReducedMotion()) {
        return ''; // No animation class on mobile
    }
    return animationClass;
};
